/**
 * 백엔드 가입 API(`POST /v1/users/signup` · `/confirm` · `/resend`)의 wire 계약 인용과 응답 판정.
 *
 * 계약 정본은 백엔드가 소유한다 — 교차 레포 정본: dronesquare-backend capability `users-api`
 * (`openspec/specs/users-api/spec.md` 「가입 API — 가입·확정·재전송」·「가입 완료는 `confirm` 요청 안에서 끝난다」,
 * 실패 본문 `{"detail","code"}` 는 `openspec/specs/envelope/spec.md` 「실패의 도메인 코드」).
 * 경로·본문·판정표가 정본과 어긋나면 정본을 따라 이 파일을 고친다.
 *
 * 판정 재료는 status 와 실패 본문의 `code`(IdP 예외 이름) 둘뿐이다. `detail` 은 운영자용 문장이라 화면에 싣지 않는다
 * (웹 스펙 `signup`).
 */

/** main-api base(`…/v1`) 기준 상대 경로. 셋 다 무인증 `POST` 다. */
export const SIGNUP_PATH = "users/signup";
export const SIGNUP_CONFIRM_PATH = "users/signup/confirm";
export const SIGNUP_RESEND_PATH = "users/signup/resend";
/** 로그인 뒤 등록 여부를 읽는 경로(인증 `GET`). 미등록이면 `401 {"error":"UserNotRegistered"}` 다(백엔드 users-api). */
export const USERINFO_PATH = "users/userinfo";
export const USER_NOT_REGISTERED = "UserNotRegistered";

/** `POST users/signup` 본문 — 오늘 Cognito 에 싣던 것과 같은 값이다. */
export type SignupRequestBody = {
  email: string;
  password: string;
  name: string;
  phone_number: string;
  job: string;
  company: string;
  field: string;
};

/**
 * 가입·확정·재전송 응답의 판정 결과 — 셋 중 하나다.
 * - `done`: 2xx. 다음 단계로 간다.
 * - `refused`: 4xx. 사용자가 입력을 고쳐야 끝난다. `code` 는 IdP 예외 이름(없으면 `null`)이다.
 * - `retry`: `503`·그 밖의 5xx·응답 없음. 같은 요청을 다시 보내면 닫힌다(백엔드가 멱등·재진입을 보장한다).
 */
export type SignupOutcome =
  | { kind: "done" }
  | { kind: "refused"; status: number; code: string | null }
  | { kind: "retry" };

export const SIGNUP_DONE: SignupOutcome = { kind: "done" };
export const SIGNUP_RETRY: SignupOutcome = { kind: "retry" };

const isSuccessStatus = (status: number) => status >= 200 && status < 300;
const isClientErrorStatus = (status: number) => status >= 400 && status < 500;

const parseJsonOrNull = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/**
 * 실패 본문의 `code`. 본문은 파싱된 값이나 원문 문자열을 받는다 — JSON 이 아니거나 비었거나 `code` 가 문자열이 아니면 `null`.
 */
export function readSignupFailureCode(body: unknown): string | null {
  const parsed = typeof body === "string" ? parseJsonOrNull(body) : body;
  if (typeof parsed !== "object" || parsed === null) return null;
  const code = (parsed as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

/**
 * 응답 → 결과 (design D1).
 *
 * | 응답 | 결과 |
 * |---|---|
 * | 2xx | `done` |
 * | 4xx | `refused` + 본문 `code` |
 * | `503`, 그 밖의 5xx, 그 밖의 status | `retry` |
 *
 * 네트워크 실패는 응답이 없으므로 호출부(repository)가 `retry` 로 접는다.
 */
export function classifySignupResponse(status: number, body: unknown): SignupOutcome {
  if (isSuccessStatus(status)) return SIGNUP_DONE;
  if (isClientErrorStatus(status)) {
    return { kind: "refused", status, code: readSignupFailureCode(body) };
  }
  return SIGNUP_RETRY;
}

/**
 * 로그인한 계정의 등록 상태 — `unknown` 은 화면에 아무 영향도 주지 않는 결과다(가입 마무리를 띄우지 않는다).
 * 확정은 됐는데 완료가 끝나지 않은 계정(`unregistered`)만 가입 마무리의 대상이다.
 */
export type RegistrationStatus = "registered" | "unregistered" | "unknown";

/**
 * `GET users/userinfo` 응답 → 등록 상태. 2xx 는 `registered`, `401` 이면서 실패 본문의 `error` 가 `UserNotRegistered` 면
 * `unregistered`, 그 밖(토큰 문제의 통일 `401 {"detail":"Unauthorized"}`·5xx·응답 없음)은 `unknown` 이다.
 */
export function classifyRegistrationResponse(status: number, body: unknown): RegistrationStatus {
  if (isSuccessStatus(status)) return "registered";
  if (status !== 401) return "unknown";
  const parsed = typeof body === "string" ? parseJsonOrNull(body) : body;
  const error = typeof parsed === "object" && parsed !== null ? (parsed as { error?: unknown }).error : undefined;
  return error === USER_NOT_REGISTERED ? "unregistered" : "unknown";
}
